import React, { useEffect, useState } from 'react';
import { UserProfile } from '../services/userService';
import { getRankProgress, getRankImagePath, getTierColor } from '../services/ranksService';
import './WelcomeBackSection.css';

interface WelcomeBackSectionProps {
  user: UserProfile | any;
  userEmail: string;
}

const WelcomeBackSection: React.FC<WelcomeBackSectionProps> = ({ user, userEmail }) => {
  const [animateStats, setAnimateStats] = useState(false);
  const [hoveredStat, setHoveredStat] = useState<string | null>(null);
  const updatedUser = user;
  
  // Calculate stats - handle both old and new user structure
  const gamesPlayed = updatedUser.gameStats?.totalGames || updatedUser.gamesPlayed || 0;
  const accuracy = (updatedUser.gameStats?.questionsAnswered || updatedUser.totalAnswers) > 0 
    ? Math.round(((updatedUser.gameStats?.correctAnswers || updatedUser.correctAnswers) / (updatedUser.gameStats?.questionsAnswered || updatedUser.totalAnswers)) * 100)
    : 0;
  const currentStreak = updatedUser.gameStats?.streak || updatedUser.bestStreak || 0;
  const bestStreak = updatedUser.gameStats?.bestStreak || updatedUser.bestStreak || 0;
  const totalPoints = updatedUser.gameStats?.totalPoints || updatedUser.totalPoints || 0;
  
  // Calculate biblical rank progress using centralized service
  const rankProgress = getRankProgress(totalPoints);
  
  // Get rank visual elements
  const rankImagePath = getRankImagePath(rankProgress.currentRank.name);
  const tierColor = getTierColor(rankProgress.currentRank.tier);
  
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateStats(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);
  
  const getMotivationalMessage = () => {
    if (accuracy >= 80) return "Outstanding Performance! 🌟";
    if (accuracy >= 60) return "Great Progress! Keep it up! 💪";
    if (currentStreak >= 7) return "Amazing Streak! You're on fire! 🔥";
    if (rankProgress.currentRank.tier === 'transcendence') return "Biblical Master! 📖";
    if (rankProgress.currentRank.tier === 'mastery') return "Approaching Mastery! ⭐";
    return "Welcome back, Champion! 🏆";
  };
  

  return (
    <div className="welcome-back-container">
      <div className="welcome-back-header">
        <div className="welcome-back-greeting">
          <h2 className="welcome-title">
            Welcome back, <span className="username-highlight">{updatedUser.displayName || userEmail.split('@')[0]}</span>!
          </h2>
          <p className="motivational-message">{getMotivationalMessage()}</p>
        </div>
        
        {/* Biblical Rank Progress */}
        <div className="rank-progress-section">
          <div className="rank-info">
            <div className="rank-image-container">
              <img 
                src={rankImagePath} 
                alt={rankProgress.currentRank.name} 
                className="rank-image"
              />
            </div>
            <div className="rank-details">
              <p className="rank-title" style={{ color: tierColor }}>{rankProgress.currentRank.name.toUpperCase()}</p>
              <p className="rank-tier">{rankProgress.currentRank.tier.charAt(0).toUpperCase() + rankProgress.currentRank.tier.slice(1)} Tier</p>
              <div className="rank-progress-bar">
                <div 
                  className="rank-progress-fill"
                  style={{ 
                    width: `${rankProgress.progressPercentage}%`,
                    backgroundColor: tierColor
                  }}
                >
                  <div className="rank-glow"></div>
                </div>
              </div>
              <p className="rank-points-text">
                {rankProgress.progressText}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Grid */}
      <div className={`stats-grid ${animateStats ? 'animate-in' : ''}`}>
        <div 
          className={`stat-card stat-games ${hoveredStat === 'games' ? 'hovered' : ''}`}
          onMouseEnter={() => setHoveredStat('games')}
          onMouseLeave={() => setHoveredStat(null)}
        >
          <div className="stat-content">
            <span className="stat-value">{gamesPlayed}</span>
            <span className="stat-label">Games Played</span>
          </div>
          <div className="stat-sparkline">
            <svg viewBox="0 0 100 40" className="sparkline-svg">
              <path d="M0,30 L20,25 L40,20 L60,22 L80,15 L100,10" stroke="#8b5cf6" strokeWidth="2" fill="none" />
            </svg>
          </div>
        </div>
        
        <div 
          className={`stat-card stat-accuracy ${hoveredStat === 'accuracy' ? 'hovered' : ''}`}
          onMouseEnter={() => setHoveredStat('accuracy')}
          onMouseLeave={() => setHoveredStat(null)}
        >
          <div className="stat-content">
            <span className="stat-value">{accuracy}%</span>
            <span className="stat-label">Accuracy</span>
          </div>
          <div className="accuracy-ring">
            <svg viewBox="0 0 36 36" className="circular-chart">
              <path className="circle-bg"
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path className="circle"
                strokeDasharray={`${accuracy}, 100`}
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
          </div>
        </div>
        
        <div 
          className={`stat-card stat-streak ${hoveredStat === 'streak' ? 'hovered' : ''}`}
          onMouseEnter={() => setHoveredStat('streak')}
          onMouseLeave={() => setHoveredStat(null)}
        >
          <div className="stat-content">
            <span className="stat-value">{bestStreak}</span>
            <span className="stat-label">Best Streak</span>
          </div>
          <div className="streak-flames">
            {[...Array(Math.min(bestStreak, 5))].map((_, i) => (
              <span key={i} className={`flame flame-${i + 1}`}>🔥</span>
            ))}
          </div>
        </div>
        
        <div 
          className={`stat-card stat-points ${hoveredStat === 'points' ? 'hovered' : ''}`}
          onMouseEnter={() => setHoveredStat('points')}
          onMouseLeave={() => setHoveredStat(null)}
        >
          <div className="stat-content">
            <span className="stat-value">{totalPoints.toLocaleString()}</span>
            <span className="stat-label">Total Points</span>
          </div>
          <div className="points-stars">
            {[...Array(5)].map((_, i) => (
              <span key={i} className={`star ${i < Math.min(rankProgress.currentRank.level, 5) ? 'filled' : ''}`}>⭐</span>
            ))}
          </div>
        </div>
      </div>
      
      
      {/* Floating particles effect */}
      <div className="particles-container">
        {[...Array(20)].map((_, i) => (
          <div key={i} className={`particle particle-${i % 4}`}></div>
        ))}
      </div>
    </div>
  );
};

export default WelcomeBackSection;